import { Flex, Table } from "antd"
import { useMemo } from "react"
import { TableType } from "src/api/graphql/graphql"
import { addDynamicStyledText } from "src/pages/DataAssets/views/Expectations/Expectation/utils/addDynamicStyledText"
import { ParamTypes } from "src/pages/DataAssets/views/Expectations/Expectation/Expectation"

type TableResultProps = {
  data: TableType
}

type ParsedTableData = {
  headerRow: Array<string>
  rows: Array<Record<string, string>>
  params: Record<string, unknown>
  template: string
}

/**
 * Table result renderer.
 *
 * Takes in `data`, which should be the shape of an instance of rendereded content's
 * `value` field.
 *
 * The result will be a paginated table of values.
 */
export function TableRenderComponent({ data }: TableResultProps): JSX.Element | null {
  const { headerRow, rows, params, template } = useMemo(() => parseData(data), [data])
  const columns = headerRow.map((title) => ({ title, key: title, dataIndex: title }))

  if (!rows.length) {
    return null
  }

  return (
    <Flex vertical>
      {addDynamicStyledText({ template, params: params as ParamTypes, getDynamicStyleTag: (text) => text })}
      <Table
        columns={columns}
        dataSource={rows}
        pagination={{ pageSize: 10, showSizeChanger: false }}
        scroll={{ x: "max-content" }}
      />
    </Flex>
  )
}

function parseData(data: TableType): ParsedTableData {
  const { headerRow, params, table, template } = data
  const parsedHeaderRow = (headerRow || []).map(parseOptionalJSONObject).map((obj) => `${obj.value}`)
  const parsedTableRows = (table || [])
    .map((row) => (row || []).map(parseOptionalJSONObject))
    .map((row) =>
      row.reduce((acc: Record<string, string>, cell: Record<string, unknown>, index: number) => {
        const key = parsedHeaderRow[index]
        return {
          ...acc,
          [key]: `${cell.value}`,
        }
      }, {}),
    )

  return {
    headerRow: parsedHeaderRow,
    rows: parsedTableRows,
    params: parseOptionalJSONObject(params),
    template: template || "",
  }
}

function parseOptionalJSONObject(value: string | undefined | null): Record<string, unknown> {
  return JSON.parse(value || "{}")
}
